import { z } from 'zod';

import { TOOL_APPROVALS, TOOL_RISKS, TOOL_SIDE_EFFECTS } from '../tool-capability.js';

/**
 * The Tool Registry as the SPA sees it - INT-002.
 *
 * **The provider bindings do not cross this wire, and that is the point.**
 * ADR-0001 keeps a provider's own identifier inside the binding, and
 * `pipedream-connect.md` §6.8 gives the reason: once anything outside the
 * gateway references `pipedream.some_action_123`, migrating providers stops
 * being a data change. A browser is as outside as it gets. So a capability is
 * published by its canonical name and the **apps** that serve it - which is
 * everything the Integrations screen needs to answer "what does connecting
 * HubSpot get me" - and `providerActionId` stays on the server.
 *
 * `inputSchema` is omitted for a related reason: nothing in the SPA invokes a
 * tool, so publishing a JSON Schema document would be shipping a payload for a
 * caller that does not exist. It is on `ToolCapability` for the runtime.
 *
 * No `workspace_id` anywhere: the catalogue is global. `0006_tool_registry`
 * carries that argument.
 */
export const toolCapabilitySchema = z
  .object({
    /** The dotted canonical name a skill references: `crm.contacts.search`. */
    id: z.string(),
    version: z.number().int().positive(),
    domain: z.string(),
    description: z.string(),
    sideEffect: z.enum(TOOL_SIDE_EFFECTS),
    risk: z.enum(TOOL_RISKS),
    approval: z.enum(TOOL_APPROVALS),
    dataClasses: z.array(z.string()).readonly(),
    /**
     * Which connected apps can perform it, by `app_key`.
     *
     * Deduplicated and ordered by the binding's priority, so the first entry is
     * the one the gateway would pick. A caller must not read anything more into
     * the order than that - which provider serves it is not published.
     */
    apps: z.array(z.string()).readonly(),
  })
  .readonly();

export type ToolCapabilitySummary = z.infer<typeof toolCapabilitySchema>;

export const toolListResponseSchema = z
  .object({ tools: z.array(toolCapabilitySchema).readonly() })
  .readonly();

export type ToolListResponse = z.infer<typeof toolListResponseSchema>;

export const toolResponseSchema = z.object({ tool: toolCapabilitySchema }).readonly();

export type ToolResponse = z.infer<typeof toolResponseSchema>;
